package org.teddapp.project.ui.notes

import org.teddapp.project.vault.Vault
import org.teddapp.project.vault.VaultCipher

/**
 * Une note verrouillée : son texte chiffré par une phrase secrète, à la manière du
 * Trousseau (PBKDF2 puis AES-256-GCM). Tout tient dans le texte de la note :
 *
 *     tedd-lock:1:<sel>:<itérations>:<texte chiffré>
 *
 * Chaque note a son sel ; rien d'autre n'est stocké, ni la phrase ni son empreinte. Le
 * texte chiffré voyage avec la synchronisation comme n'importe quel texte : le serveur
 * ne voit jamais le clair. Le titre, lui, reste lisible, pour la liste.
 */
internal object NoteLock {
    private const val PREFIX = "tedd-lock:1:"

    /** Le chiffrement n'est pas encore là partout (navigateur, iOS). */
    val available: Boolean get() = VaultCipher.isAvailable

    fun isLocked(body: String): Boolean = body.startsWith(PREFIX)

    /**
     * Une note ouverte : sa clé, gardée en mémoire le temps qu'elle reste à l'écran, pour
     * rechiffrer chaque modification sans redériver la clé (ce qui prend une demi-seconde).
     */
    class Unlocked(val salt: String, val iterations: Int, val key: String, val plain: String)

    /** Chiffre [plain] avec une phrase neuve et un sel neuf. Coûteux : hors du fil d'interface. */
    fun lock(passphrase: String, plain: String, iterations: Int = Vault.ITERATIONS): Pair<String, Unlocked> {
        val salt = VaultCipher.randomSalt()
        val key = VaultCipher.deriveKey(passphrase, salt, iterations)
        val u = Unlocked(salt, iterations, key, plain)
        return reseal(u, plain) to u
    }

    /** Ouvre la note ; null si la phrase n'est pas la bonne. Coûteux, comme [lock]. */
    fun unlock(passphrase: String, body: String): Unlocked? {
        if (!isLocked(body)) return null
        val parts = body.removePrefix(PREFIX).split(':', limit = 3)
        if (parts.size != 3) return null
        val iterations = parts[1].toIntOrNull() ?: return null
        val key = VaultCipher.deriveKey(passphrase, parts[0], iterations)
        val plain = VaultCipher.open(key, parts[2]) ?: return null
        return Unlocked(parts[0], iterations, key, plain)
    }

    /** Le texte [plain] chiffré avec la clé déjà dérivée : rapide, pour chaque enregistrement. */
    fun reseal(u: Unlocked, plain: String): String = PREFIX + u.salt + ":" + u.iterations + ":" + VaultCipher.seal(u.key, plain)

    /**
     * Ce qui empêche de verrouiller : les dessins et les images ne sont pas dans le texte
     * et ne seraient pas chiffrés. Mieux vaut le dire que laisser croire qu'ils le sont.
     */
    fun refusal(body: String, attachments: String?): String? = when {
        org.teddapp.project.ui.notes.ink.NoteAttachments.inks(attachments).isNotEmpty() ||
            body.split('\n').any { MarkdownEdits.inkIdOf(it) != null } ->
            "Une note qui contient un dessin ne se verrouille pas : le dessin ne serait pas chiffré."
        body.split('\n').any { MarkdownEdits.fileIdOf(it) != null } ->
            "Une note qui contient une image ne se verrouille pas : l'image ne serait pas chiffrée."
        else -> null
    }
}
